import '../styles/paper.css';
import { DISCORD_GLYPH } from './board';
import { el } from './format';
import type { PaperWords } from './paperWords';

const CONFETTI = ['#b8352b', '#edc27a', '#f3e6c8', '#2f4a2e', '#c8784a'];

/**
 * The morning paper: La Gazzetta del Grand Premio, dropped a little askew on top of the main page (blurred
 * behind it) the first time a player opens the game the day after a race. Its words come from paperWords;
 * the photo (pressPhoto) arrives a moment later and drops into its frame.
 */
export class MorningPaper {
  private layer: HTMLElement;
  private shareBtn: HTMLButtonElement;
  private goBtn: HTMLButtonElement;
  private img: HTMLImageElement | null = null;
  private onClose: () => void = () => {};
  private refit = (): void => this.fit();
  private onShare: (say: (label: string) => void) => void = () => {};

  constructor(parent: HTMLElement) {
    this.layer = el('div', 'paper-layer interactive', parent);
    this.layer.hidden = true;
    this.layer.setAttribute('role', 'dialog');
    this.layer.setAttribute('aria-label', 'La Gazzetta del Grand Premio: yesterday’s race');
    this.shareBtn = document.createElement('button');
    this.goBtn = document.createElement('button');
  }

  get open(): boolean {
    return !this.layer.hidden;
  }

  /**
   * `join`: il paddock, the Grand Premio server on Discord (left out: no line, as in the picture posted to
   * Discord servers, or once it's been tapped).
   */
  show(w: PaperWords, on: { share: (say: (label: string) => void) => void; close: () => void; join?: () => void }): void {
    this.onClose = on.close;
    this.onShare = on.share;
    const L = this.layer;
    L.replaceChildren();
    const paper = el('article', 'paper', L);
    const top = el('div', 'gz-top', paper);
    for (const t of w.top) el('span', '', top, t);
    const mast = el('h1', 'gz-mast', paper);
    el('small', '', mast, 'LA');
    mast.append(' GAZZETTA ');
    el('small', '', mast, 'DEL GRAND PREMIO');
    el('div', 'gz-rule', paper);
    const dl = el('div', 'gz-dateline', paper);
    for (const t of w.dateline) el('span', '', dl, t);
    el('h2', 'gz-head', paper, w.head);
    if (w.deck) el('p', 'gz-deck', paper, w.deck);
    const cols = el('div', 'gz-cols', paper);
    const fig = el('figure', 'gz-photo', cols);
    const frame = el('div', 'gz-frame', fig);
    this.img = el('img', '', frame);
    this.img.alt = w.caption;
    this.img.hidden = true;
    if (w.champion) el('span', 'gz-thatsyou', frame, 'that’s you!');
    el('figcaption', '', fig, w.caption);
    const res = el('div', 'gz-results', cols);
    el('h3', '', res, 'the podium');
    const ol = el('ol', '', res);
    for (const p of w.podium) {
      const li = el('li', '', ol);
      el('b', '', li, String(p.place));
      el('span', '', li, p.name);
      el('span', '', li, p.time);
    }
    el('p', '', res, w.drivers);
    if (w.story) el('div', 'gz-story', res, w.story);
    if (w.yours) {
      const y = el('section', 'gz-yours', paper);
      el('div', 'gz-kick', y, w.yours.kick);
      const big = el('div', 'gz-big', y, `${w.yours.big} `);
      el('small', '', big, w.yours.of);
      el('div', 'gz-line', y, w.yours.line);
      const st = el('div', 'gz-stamps', y);
      for (const s of w.yours.stamps) el('span', 'gz-stamp', st, s);
      if (w.yours.rival) {
        const r = el('div', 'gz-rival', y);
        el('b', '', r, w.yours.rival.name);
        r.append(w.yours.rival.text);
      }
      if (w.yours.gold) {
        const g = el('div', 'gz-gold', y);
        el('i', '', g, '1');
        g.append(w.yours.gold);
      }
      // Il paddock, one line at the column's foot (kept under the stamps: fit()).
      if (on.join) {
        const p = this.joinButton('gz-padline', y, on.join);
        p.insertAdjacentHTML('beforeend', `<span class="discord-mark">${DISCORD_GLYPH}</span>`);
        el('b', '', p, 'il paddock');
        el('span', 'words', p, 'talk it over · the championship is coming');
        el('i', '', p, 'join ▸');
      }
    } else if (w.away) {
      const a = el('section', 'gz-away', paper);
      el('b', '', a, w.away.head);
      a.append(w.away.text);
    }
    // I furbetti: yesterday's made-up laps the stewards caught (net/stewards.ts), one line each.
    if (w.caught.length) {
      const c = el('section', 'gz-shame', paper);
      const k = el('div', 'gz-kick', c);
      el('b', '', k, 'i furbetti');
      el('span', 'gz-stamp', k, 'squalificato');
      el('span', 'gz-sub', k, 'the stewards’ corner of shame');
      for (const x of w.caught) {
        const p = el('p', '', c);
        el('b', '', p, x.name);
        p.append(x.text);
      }
    }
    if (on.join && !w.yours) {
      const ad = this.joinButton('gz-ad', paper, on.join);
      el('span', 'gz-stamp', ad, 'avviso');
      const t = el('span', 'words', ad);
      el('b', '', t, 'il paddock is open');
      t.append(' · talk about the race · the championship is coming');
      const go = el('span', 'go', ad);
      go.innerHTML = `<span class="discord-mark">${DISCORD_GLYPH}</span>join ▸`;
    }
    const today = el('div', 'gz-today', paper);
    const [t0, t1] = w.today;
    const lead = el('span', '', today);
    el('b', '', lead, 'today');
    lead.append(t0.replace(/^today/, ''));
    el('span', '', today, t1);
    const actions = el('div', 'paper-actions', L);
    this.shareBtn = el('button', 'gz-share', actions, 'share ↗');
    this.shareBtn.type = 'button';
    this.shareBtn.onclick = () => this.onShare((label) => (this.shareBtn.textContent = label));
    this.goBtn = el('button', 'gz-go', actions, 'today’s race ▸');
    this.goBtn.type = 'button';
    this.goBtn.onclick = () => this.close();
    if (w.champion) this.confetti();
    L.hidden = false;
    this.fit();
    addEventListener('resize', this.refit);
    this.goBtn.focus({ preventScroll: true });
  }

  /** Il paddock's line: the Grand Premio server on Discord. */
  private joinButton(cls: string, parent: HTMLElement, join: () => void): HTMLButtonElement {
    const b = el('button', cls, parent) as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('aria-label', 'Il paddock: join the Grand Premio server on Discord');
    b.onclick = join;
    return b;
  }

  /** A screen too short for the whole page (a phone on its side): the paper scaled down to fit above its buttons. */
  private fit(): void {
    const paper = this.layer.querySelector<HTMLElement>('.paper');
    const actions = this.layer.querySelector<HTMLElement>('.paper-actions');
    if (!paper || !actions) return;
    paper.style.setProperty('--fit', '1');
    // Il paddock's line starts under your race's stamps, which can reach lower than the column's words.
    const line = paper.querySelector<HTMLElement>('.gz-padline');
    const stamps = paper.querySelector<HTMLElement>('.gz-stamps');
    if (line && stamps) {
      line.style.marginTop = '';
      const under = stamps.offsetTop + stamps.offsetHeight + 4 - line.offsetTop;
      if (under > 0) line.style.marginTop = `${(parseFloat(getComputedStyle(line).marginTop) + under).toFixed(1)}px`;
    }
    paper.style.marginBlock = '';
    const cs = getComputedStyle(this.layer);
    const room = this.layer.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - actions.offsetHeight - parseFloat(cs.rowGap || '0') - 8;
    const h = paper.offsetHeight;
    if (h <= room) return;
    const fit = Math.max(0.4, room / h);
    paper.style.setProperty('--fit', fit.toFixed(3));
    // Scaling doesn't give the space back: the margins do.
    paper.style.marginBlock = `${(-(h - h * fit) / 2).toFixed(1)}px`;
  }

  /** The press photo, once drawn. */
  setPhoto(src: string): void {
    if (!this.img || !src) return;
    this.img.src = src;
    this.img.hidden = false;
  }

  close(): void {
    if (this.layer.hidden) return;
    removeEventListener('resize', this.refit);
    this.layer.hidden = true;
    this.layer.replaceChildren();
    this.img = null;
    this.onClose();
  }

  /** Confetti for the winner, in the poster's colours: over the page around the paper, a few pieces on it. */
  private confetti(): void {
    const c = el('div', 'paper-confetti', this.layer);
    let seed = 7;
    const rnd = (): number => (seed = (seed * 16807) % 2147483647) / 2147483647;
    const narrow = innerWidth < 600;
    for (let n = 0; n < (narrow ? 22 : 64); ) {
      const x = rnd() * 100;
      const y = rnd() * 100;
      if (narrow && y > 88) continue;
      const onPaper = narrow ? y > 6 && x > 5 && x < 95 : x > 27 && x < 73 && y > 3 && y < 90;
      if (onPaper && rnd() > 0.12) continue;
      const i = el('i', '', c);
      i.style.cssText = `left:${x.toFixed(1)}%;top:${y.toFixed(1)}%;background:${CONFETTI[n % 5]};transform:rotate(${Math.round(rnd() * 180)}deg) scale(${(0.7 + rnd() * 0.6).toFixed(2)});animation-delay:${(rnd() * 0.5).toFixed(2)}s`;
      n++;
    }
  }
}
